import React, { useEffect, useState, useRef } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Modules from './modules/index';
import { deviceDetails } from 'Helpers/external';

import { useDispatch } from 'react-redux';

import { setOsVersion, setOsName, setBrowserName, setFullVersion } from 'Features/generalSlice';
import UnderMaintenance from 'Modal/UnderMaintenance/UnderMaintenance.lazy';
import CookiesModal from 'Modal/CookiesModal/CookiesModal.lazy';

function App() {
  const dispatch = useDispatch();
  const rtcDetails = deviceDetails();
  const [downTimeMessage, setDownTimeMessage] = useState('');
  const [showConsentBanner, setShowConsentBanner] = useState(false);
  const pathSegments = window.location.pathname.split('/');
  const interviewID = pathSegments[pathSegments.length - 1];
  const clarityInitializedRef = useRef(false); // Use ref instead of state
  const [pageLoaded, setPageLoaded] = useState(false);
  const [theme, setTheme] = useState('');

  dispatch(setOsVersion(rtcDetails.osVersion));
  dispatch(setOsName(rtcDetails.osName));
  dispatch(setBrowserName(rtcDetails.browserName));
  dispatch(setFullVersion(rtcDetails.fullVersion));

  const consentAllowedRoutes = ['/video-interview', '/kit-preview'];
  const shouldShowConsentForThisRoute = consentAllowedRoutes.some((route) =>
    window.location.pathname.startsWith(route)
  );

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  const initializeClarity = () => {
    if (process.env.REACT_APP_ENV !== 'production') {
      console.log('Clarity initialization skipped (non-production environment)');
      return;
    }
    if (clarityInitializedRef.current) {
      return;
    }

    // Initialize clarity queue BEFORE script loads
    window.clarity =
      window.clarity ||
      function () {
        (window.clarity.q = window.clarity.q || []).push(arguments);
      };

    // Queue consent call BEFORE the script loads
    window.clarity('consent', {
      ad_Storage: 'granted',
      analytics_Storage: 'granted',
    });

    // Now load the Clarity script
    (function (c, l, a, r, i, t, y) {
      t = l.createElement(r);
      t.async = 1;
      t.src = 'https://www.clarity.ms/tag/' + i;

      t.onerror = function () {
        console.error('Failed to load Clarity script');
        clarityInitializedRef.current = false;
      };

      t.onload = function () {
        console.log('Clarity script loaded successfully');
      };

      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', 'vpxi51vr5m');

    clarityInitializedRef.current = true;

    // Wait for Clarity to be fully loaded, then set custom tags
    let attempts = 0;
    const checkClarity = setInterval(() => {
      attempts++;

      if (window.clarity && typeof window.clarity === 'function' && !window.clarity.q) {
        clearInterval(checkClarity);
        try {
          // Set custom tag for interview ID
          window.clarity('set', 'interviewID', interviewID.toString());
        } catch (error) {
          console.error('Error setting Clarity metadata:', error);
        }
      } else if (attempts >= 600) {
        clearInterval(checkClarity);
      }
    }, 100);
  };

  useEffect(() => {
    if( !shouldShowConsentForThisRoute ) {
      return ;
    }
    const rawConsent = getCookie('cookieConsent');

    if (rawConsent) {
      try {
        const consent = JSON.parse(decodeURIComponent(rawConsent));

        // Initialize Clarity if consent was previously granted
        if (consent.analytics_Storage === 'granted') {
          if (!clarityInitializedRef.current) {
            initializeClarity();
          }
        }
      } catch (e) {
        console.error('Error parsing cookie consent:', e);
        setShowConsentBanner(true);
      }
    } else {
      setShowConsentBanner(true);
    }
  }, [interviewID]);

  const handleAcceptCookies = () => {
    setShowConsentBanner(false);
    const consentValue = JSON.stringify({
      necessary: true,
      functional: true,
      ad_Storage: 'granted',
      analytics_Storage: 'granted',
    });
    document.cookie = 'cookieConsent=' + encodeURIComponent(consentValue) + '; path=/; max-age=' + 30 * 24 * 60 * 60;

    initializeClarity();
  };

  const handleDeclineCookies = () => {
    setShowConsentBanner(false);
    const consentValue = JSON.stringify({
      necessary: false,
      functional: false,
      ad_Storage: 'denied',
      analytics_Storage: 'denied',
    });
    document.cookie = 'cookieConsent=' + encodeURIComponent(consentValue) + '; path=/; max-age=' + 30 * 24 * 60 * 60;
    // Don't initialize Clarity if declined
  };

  return (
    <>
      {downTimeMessage.length === 0 ? (
        <HelmetProvider>
          <div className="App">
            <Helmet>
              <title>Jobma - Interview</title>
              <meta name="description" content="Jobma Interview WebApp" />
              <meta name="theme-color" content="#008f68" />
              <link rel="icon" type="image/png" href="https://www.jobma.com/favicon.ico" sizes="16x16" />
              {process.env.REACT_APP_ENV === 'production' && (
                <>
                  <script async src="https://www.googletagmanager.com/gtag/js?id=G-QP1V0RNJZH"></script>
                  <script>
                    {`window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);} gtag('js', new Date());
                    gtag('config', 'G-QP1V0RNJZH');`}
                  </script>
                </>
              )}
            </Helmet>
            <Modules setDownTimeMessage={setDownTimeMessage} pageLoaded = {pageLoaded} setPageLoaded = {setPageLoaded} setTheme = {setTheme} />
            {showConsentBanner && shouldShowConsentForThisRoute && pageLoaded && theme !== '' && <CookiesModal onAccept={handleAcceptCookies} onDecline={handleDeclineCookies} theme = {theme} setShowConsentBanner={setShowConsentBanner} initializeClarity ={initializeClarity}/>}
          </div>
        </HelmetProvider>
      ) : (
        <UnderMaintenance downTimeMessage={downTimeMessage} />
      )}
    </>
  );
}

export default App;
